import { ExplainedControl } from "@/shared/components/ExplainedControl";
import { ingestionRuleIdOf } from "@/shared/domain/ingestionRules";
import { type TriggerRecord } from "@/shared/domain/triggers";
import { useToggleTrigger } from "@/shared/domain/triggers/hooks";
import { Switch } from "@/shared/foundation/primitives";

/**
 * A trigger's Enabled switch in the triggers list. It shows the value a
 * toggle in flight is sending, and only it is disabled meanwhile. A trigger an
 * ingestion rule owns is turned on and off through the rule, so its switch is
 * always disabled.
 *
 * `onToggle` and `onError` come from the page, which reports a failure in its
 * alert.
 */
export function TriggerEnabledSwitch({
  trigger,
  onToggle,
  onError,
}: {
  trigger: TriggerRecord;
  onToggle: () => void;
  onError: (error: Error) => void;
}) {
  const { checked, pending, toggle } = useToggleTrigger(trigger, onError);
  const ruleOwned = ingestionRuleIdOf(trigger) !== null;

  let explanation = checked ? "Disable trigger" : "Enable trigger";
  if (ruleOwned) {
    explanation =
      "Managed by an ingestion rule; turn auto-ingest off on the rule instead";
  }

  return (
    <ExplainedControl explanation={explanation} disabled={pending || ruleOwned}>
      {(describedBy) => (
        <Switch
          checked={checked}
          disabled={pending || ruleOwned}
          onCheckedChange={(enabled) => {
            onToggle();
            toggle(enabled);
          }}
          aria-label={`${trigger.name} enabled`}
          aria-describedby={describedBy}
        />
      )}
    </ExplainedControl>
  );
}
